import metrics from 'metrics';
import instrumentMethods from 'instrument-methods';

import { MORE_OPTIONS, GET_RESULTS_SUCCESS, RESULTS_PAGE_LOAD } from 'store/results/results';
import { GET_ACCOUNT_SUCCESS, UPDATE_AUTHENTICATED, UPDATE_USER_CLIENT_ID } from 'store/user/user';
import { LAST_SEARCH_CLICKED } from 'store/lastSearch/lastSearch';
import {
  DID_YOU_MEAN_CLICKED, SORT_MODE_CHANGED,
  QUICK_SEARCH_CLASSIFICATION_CLICKED, QUICK_SEARCH_CITY_CLICKED, QUICK_SEARCH_GRADUATE_CLICKED,
  JOB_CLASSIFICATION_CLICKED, JOB_SUBCLASSIFICATION_CLICKED,
  JOB_LOCATION_CLICKED, JOB_AREA_CLICKED, JOB_SUBURB_CLICKED,
  WHERE_FIELD_FOCUSED, WHERE_FIELD_BLANKED, WHERE_FIELD_TYPED,
  WHERE_FIELD_RECENT_SHOWN, WHERE_FIELD_SUGGESTED_SHOWN,
  WHERE_FIELD_RECENT_SELECTED, WHERE_FIELD_SUGGESTED_SELECTED, WHERE_FIELD_AUTOSUGGESTED_SELECTED
} from 'store/search/search';
import { SAVED_SEARCH_CLICKED } from 'store/dashboard/dashboard';
import { LOCATION_CHANGED, TRACKED_LINK_CLICKED, CAMPAIGN_LINK_CLICKED } from 'store/location/location';
import { HOME_PAGE_LOADED } from 'store/home/home';
import { TOGGLE_PEEK_DOWN, DISMISS_PEEK_DOWN } from 'store/peekDown/peekDown';
import { SAVE_JOB_SUCCESS, REMOVE_SAVE_JOB_SUCCESS } from 'store/savedJobs/savedJobs';
import { SAVE_SEARCH_SUCCESS, JOB_MAIL_SUCCESS } from 'store/saveSearch/saveSearch';
import { EXPERIMENT_IMPRESSION } from 'store/experiments/experiments';
import { GET_LMIS_SUCCESS, GET_LMIS_ERROR } from 'store/lmis/lmis';

const analyticsLogger = (methodName, args) => {
  if (typeof window !== 'undefined' && window.ANALYTICS_DEBUG) {
    console.log(`analytics.${methodName}()`, ...args);
  }

  metrics.count('analytics', [`analytics-name:${methodName}`]);
};

const instrumentAnalytics = analytics => {
  return instrumentMethods(analytics, { after: analyticsLogger });
};

export default analytics => instrumentAnalytics(analytics) && (() => next => action => {
  const { type, payload } = action;
  const {
    searchPerformed,
    moreOptionsClicked,
    didYouMeanClicked,
    lastSearchClicked,
    quickSearchClassificationClicked,
    quickSearchCityClicked,
    quickSearchGraduateClicked,
    jobClassificationClicked,
    jobSubclassificationClicked,
    jobLocationClicked,
    jobAreaClicked,
    jobSuburbClicked,
    savedSearchClicked,
    userDetailsUpdated,
    locationChanged,
    searchResultsPageLoaded,
    sortModeChanged,
    homePageLoaded,
    createSaveSearchClicked,
    createJobMailClicked,
    createSavedJobClicked,
    removeSavedJobClicked,
    linkClicked,
    learnMoreOpen,
    learnMoreDismiss,
    experimentImpression,
    campaignClick,
    lmisUpdated,
    whereFieldFocused,
    whereFieldBlanked,
    whereFieldTyped,
    whereFieldRecentShown,
    whereFieldSuggestedShown,
    whereFieldRecentSelected,
    whereFieldSuggestedSelected,
    whereFieldAutosuggestedSelected
  } = analytics;

  switch (type) {
    case GET_RESULTS_SUCCESS: {
      const { query, result, userQueryId } = payload;

      searchPerformed({ query, result, userQueryId });
      break;
    }

    case UPDATE_AUTHENTICATED: {
      const { authenticated } = payload;

      userDetailsUpdated({ authenticated });
      break;
    }

    case UPDATE_USER_CLIENT_ID: {
      const { userClientId } = payload;

      userDetailsUpdated({ userClientId });
      break;
    }

    case GET_ACCOUNT_SUCCESS: {
      const { emailCorrelationId } = payload;

      userDetailsUpdated({ emailCorrelationId });
      break;
    }

    case RESULTS_PAGE_LOAD: {
      searchResultsPageLoaded();
      break;
    }

    case MORE_OPTIONS: {
      moreOptionsClicked();
      break;
    }

    case DID_YOU_MEAN_CLICKED: {
      didYouMeanClicked();
      break;
    }

    case QUICK_SEARCH_CLASSIFICATION_CLICKED: {
      quickSearchClassificationClicked();
      break;
    }

    case QUICK_SEARCH_CITY_CLICKED: {
      quickSearchCityClicked();
      break;
    }

    case QUICK_SEARCH_GRADUATE_CLICKED: {
      quickSearchGraduateClicked();
      break;
    }

    case JOB_CLASSIFICATION_CLICKED: {
      jobClassificationClicked();
      break;
    }

    case JOB_SUBCLASSIFICATION_CLICKED: {
      jobSubclassificationClicked();
      break;
    }

    case JOB_LOCATION_CLICKED: {
      jobLocationClicked();
      break;
    }

    case JOB_AREA_CLICKED: {
      jobAreaClicked();
      break;
    }

    case JOB_SUBURB_CLICKED: {
      jobSuburbClicked();
      break;
    }

    case LAST_SEARCH_CLICKED: {
      lastSearchClicked();
      break;
    }

    case SAVED_SEARCH_CLICKED: {
      savedSearchClicked();
      break;
    }

    case LOCATION_CHANGED: {
      locationChanged(payload.location);
      break;
    }

    case SORT_MODE_CHANGED: {
      sortModeChanged();
      break;
    }

    case HOME_PAGE_LOADED: {
      homePageLoaded();
      break;
    }

    case SAVE_SEARCH_SUCCESS: {
      createSaveSearchClicked();
      break;
    }

    case JOB_MAIL_SUCCESS: {
      createJobMailClicked();
      break;
    }

    case TRACKED_LINK_CLICKED: {
      const { analytics: linkName, href } = payload;

      linkClicked({ href, linkName });
      break;
    }

    case TOGGLE_PEEK_DOWN: {
      learnMoreOpen();
      break;
    }

    case DISMISS_PEEK_DOWN: {
      learnMoreDismiss();
      break;
    }

    case SAVE_JOB_SUCCESS: {
      createSavedJobClicked(payload);
      break;
    }

    case REMOVE_SAVE_JOB_SUCCESS: {
      removeSavedJobClicked(payload);
      break;
    }

    case EXPERIMENT_IMPRESSION: {
      experimentImpression(payload);
      break;
    }

    case CAMPAIGN_LINK_CLICKED: {
      campaignClick(payload);
      break;
    }

    case GET_LMIS_SUCCESS: {
      lmisUpdated(payload);
      break;
    }

    case GET_LMIS_ERROR: {
      lmisUpdated({ lmisType: 'fallback' });
      break;
    }

    case WHERE_FIELD_FOCUSED: {
      whereFieldFocused();
      break;
    }

    case WHERE_FIELD_BLANKED: {
      whereFieldBlanked();
      break;
    }

    case WHERE_FIELD_TYPED: {
      whereFieldTyped();
      break;
    }

    case WHERE_FIELD_RECENT_SHOWN: {
      whereFieldRecentShown();
      break;
    }

    case WHERE_FIELD_SUGGESTED_SHOWN: {
      whereFieldSuggestedShown();
      break;
    }

    case WHERE_FIELD_RECENT_SELECTED: {
      whereFieldRecentSelected();
      break;
    }

    case WHERE_FIELD_SUGGESTED_SELECTED: {
      whereFieldSuggestedSelected();
      break;
    }

    case WHERE_FIELD_AUTOSUGGESTED_SELECTED: {
      whereFieldAutosuggestedSelected();
      break;
    }

    default: {
      break;
    }
  }
  return next(action);
});



/** WEBPACK FOOTER **
 ** ./app/store/middleware/createAnalyticsMiddleware.js
 **/